import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import type { Dayjs } from 'dayjs';
import { TEAM_STATUS } from '@/models/references/references';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';
import TeamSummaryModel from '@/models/entities/TeamSummaryModel';

/** URN pour écrire une relation vers cette ressource (corps d'un POST / PATCH) ; un filtre prend l'id */
export const teamUrn = (teamId: id | number) => urnOf('team', teamId);

/**
 * Équipe d'une société (GET / POST / PATCH / DELETE /teams), gérée par les Dirigeants de la société.
 * teamStatus arrive avec son id (ACTIVE / ARCHIVED) ; il change par POST /team_status_histories.
 * Écriture : company (IRI, à la création seulement), name, description, color (palette TEAM_COLORS, facultative). DELETE refusé (409) si elle a déjà eu des membres.
 * activeMemberCount : nombre de membres actuels, manager compris, lu dans son résumé (TeamSummary, ressource à part :
 * fetchSummaries(teams, TEAM_SUMMARIES)), 0 tant qu'il n'est pas chargé.
 */
export default class TeamModel extends AbstractApiModel {
  protected _path = '/teams/{?id}';

  declare _tsFilters: { company?: string; teamStatus?: id; search?: string };
  declare _tsSorts: { name?: unknown };

  @doc.string declare name: string;
  @doc.string declare description: string;
  /** Couleur du tag (TeamTag), vide sans couleur */
  @doc.string declare color: string;
  /** Rempli par fetchSummaries (le résumé n'arrive pas avec l'équipe) */
  @doc.model(() => TeamSummaryModel) declare teamSummary: TeamSummaryModel;
  @doc.date declare createdAt: Dayjs;
  @doc.string declare createdBy: string;
  @doc.date declare updatedAt: Dayjs;
  @doc.string declare updatedBy: string;

  /** company arrive imbriquée ({@id, name}) ; IRI seule par prudence */
  get companyId(): number {
    const company = this.get<unknown, unknown>('company', null);
    const iri = typeof company === 'string' ? company : ((company as { '@id'?: string } | null)?.['@id'] ?? '');
    return Number(iri.split('/').pop()) || 0;
  }

  get activeMemberCount(): number {
    return this.teamSummary.activeMemberCount;
  }

  /** ACTIVE / ARCHIVED */
  get teamStatusReference(): string {
    return referenceOf('teamStatuses', this.get<unknown, unknown>('teamStatus', null))?.reference ?? '';
  }

  get teamStatusLabel(): string {
    return referenceOf('teamStatuses', this.get<unknown, unknown>('teamStatus', null))?.label ?? '';
  }

  get isActive(): boolean {
    return this.teamStatusReference === TEAM_STATUS.ACTIVE;
  }
}
